import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './Analyzer.css';

function AnalyzerList({ analyses, onViewAnalysis, onNewAnalysis }) {
  return (
    <div className="analyzer-container">
      <div className="analyzer-header">
        <div>
          <h1>Analyzer</h1>
          <p>Analyze your Tableau environments</p>
        </div>
        <button className="primary-button" onClick={onNewAnalysis}>
          + New Analysis
        </button>
      </div>

      <div className="analyses-grid">
        {analyses.map((analysis) => (
          <div key={analysis.id} className="analysis-card">
            <div className="analysis-header">
              <h3>{analysis.name}</h3>
              <span className={`status-badge ${analysis.status.toLowerCase().replace(' ', '-')}`}>
                {analysis.status}
              </span>
            </div>
            <div className="analysis-details">
              <p><strong>Environment:</strong> {analysis.environment}</p>
              <p><strong>Dashboards:</strong> {analysis.dashboards}</p>
              <p><strong>Users:</strong> {analysis.users}</p>
              <p><strong>Date:</strong> {analysis.date}</p>
            </div>
            <button
              className="secondary-button"
              onClick={() => onViewAnalysis(analysis.id)}
            >
              View Details
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalysisDetail({ analysis, onBack, onViewDashboard }) {
  return (
    <div className="analyzer-container">
      <div className="analyzer-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Analyses
        </button>
      </div>

      <div className="analysis-detail">
        <div className="detail-header">
          <h1>{analysis.name}</h1>
          <span className={`status-badge ${analysis.status.toLowerCase().replace(' ', '-')}`}>
            {analysis.status}
          </span>
        </div>

        <div className="detail-info">
          <div className="info-item">
            <label>Environment:</label>
            <span>{analysis.environment}</span>
          </div>
          <div className="info-item">
            <label>Date:</label>
            <span>{analysis.date}</span>
          </div>
        </div>

        <div className="metrics-section">
          <h2>Analysis Metrics</h2>
          <div className="metrics-grid">
            <div className="metric-box">
              <h3>{analysis.dashboards}</h3>
              <p>Total Dashboards</p>
            </div>
            <div className="metric-box">
              <h3>{analysis.users}</h3>
              <p>Active Users</p>
            </div>
            <div className="metric-box">
              <h3>{analysis.complexity}</h3>
              <p>Avg Complexity</p>
            </div>
            <div className="metric-box">
              <h3>{analysis.migrationReadiness}%</h3>
              <p>Migration Ready</p>
            </div>
          </div>
        </div>

        <div className="complexity-breakdown-section">
          <h2>Complexity Breakdown</h2>
          <div className="complexity-breakdown-grid">
            <div className="complexity-breakdown-box low">
              <h3>{analysis.dashboardDetails.filter(d => d.complexity === 'Low').length}</h3>
              <p>Low Complexity</p>
            </div>
            <div className="complexity-breakdown-box medium">
              <h3>{analysis.dashboardDetails.filter(d => d.complexity === 'Medium').length}</h3>
              <p>Medium Complexity</p>
            </div>
            <div className="complexity-breakdown-box high">
              <h3>{analysis.dashboardDetails.filter(d => d.complexity === 'High').length}</h3>
              <p>High Complexity</p>
            </div>
          </div>
        </div>

        <div className="dashboard-list-section">
          <h2>Dashboard Breakdown</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Dashboard Name</th>
                  <th>Complexity</th>
                  <th>Usage</th>
                  <th>Migration Ready</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {analysis.dashboardDetails.map((dashboard, index) => (
                  <tr key={index}>
                    <td>
                      <button
                        className="dashboard-link"
                        onClick={() => onViewDashboard(dashboard)}
                      >
                        {dashboard.name}
                      </button>
                    </td>
                    <td>
                      <span className={`complexity-badge ${dashboard.complexity.toLowerCase()}`}>
                        {dashboard.complexity}
                      </span>
                    </td>
                    <td>{dashboard.usage}</td>
                    <td>
                      <span className={dashboard.ready ? 'ready-yes' : 'ready-no'}>
                        {dashboard.ready ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => onViewDashboard(dashboard)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewAnalysis({ onCancel, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    environment: '',
    serverUrl: '',
    username: '',
    password: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="analyzer-container">
      <div className="analyzer-header">
        <button className="back-button" onClick={onCancel}>
          ← Cancel
        </button>
      </div>

      <div className="new-analysis-form">
        <h1>New Analysis</h1>
        <p>Connect to your Tableau environment and run analysis</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Analysis Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Marketing Environment"
              required
            />
          </div>

          <div className="form-group">
            <label>Environment</label>
            <select
              value={formData.environment}
              onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
              required
            >
              <option value="">Select environment</option>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
            </select>
          </div>

          <div className="form-group">
            <label>Tableau Server URL</label>
            <input
              type="url"
              value={formData.serverUrl}
              onChange={(e) => setFormData({ ...formData, serverUrl: e.target.value })}
              placeholder="https://tableau.company.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="Enter username"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Enter password"
              required
            />
          </div>

          <div className="form-actions">
            <button type="button" className="secondary-button" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="primary-button">
              Run Analysis
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DashboardDetail({ dashboard, onBack }) {
  const [timePeriod, setTimePeriod] = useState('1-month');
  const [userSearch, setUserSearch] = useState('');

  return (
    <div className="analyzer-container">
      <div className="analyzer-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Report
        </button>
      </div>

      <div className="dashboard-detail">
        <div className="detail-header">
          <h1>{dashboard.name}</h1>
          <p className="project-label">Project: {dashboard.project}</p>
        </div>

        <div className="dashboard-stats">
          <div className="stat-box-small">
            <label>TOTAL VIEWS</label>
            <h3>{dashboard.totalViews}</h3>
          </div>
          <div className="stat-box-small">
            <label>UNIQUE USERS</label>
            <h3>{dashboard.uniqueUsers}</h3>
          </div>
          <div className="stat-box-small">
            <label>FIRST ACCESS</label>
            <h3>{dashboard.firstAccess}</h3>
          </div>
          <div className="stat-box-small">
            <label>LAST ACCESS</label>
            <h3>{dashboard.lastAccess}</h3>
          </div>
        </div>

        <div className="complexity-scores">
          <h2>Complexity Analysis</h2>
          <div className="scores-grid">
            <div className="score-card tableau-complexity">
              <div className="score-header">
                <h3>Tableau Complexity</h3>
                <p>Overall dashboard complexity based on features used</p>
              </div>
              <div className="score-value">
                <span className="score-number">{dashboard.tableauComplexity}</span>
                <span className="score-max">/10</span>
              </div>
              <div className="score-bar">
                <div
                  className="score-fill tableau"
                  style={{ width: `${(dashboard.tableauComplexity / 10) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="score-card migration-complexity">
              <div className="score-header">
                <h3>Power BI Migration Complexity</h3>
                <p>Difficulty of automated migration to Power BI</p>
              </div>
              <div className="score-value">
                <span className="score-number">{dashboard.migrationComplexity}</span>
                <span className="score-max">/10</span>
              </div>
              <div className="score-bar">
                <div
                  className="score-fill migration"
                  style={{ width: `${(dashboard.migrationComplexity / 10) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="migration-challenges">
            <h3>Migration Challenges Detected</h3>
            <div className="challenges-grid">
              <div className="challenge-item">
                <span className="challenge-label">Context Filters</span>
                <span className="challenge-count">{dashboard.migrationChallenges.contextFilters}</span>
              </div>
              <div className="challenge-item">
                <span className="challenge-label">Set Actions</span>
                <span className="challenge-count">{dashboard.migrationChallenges.setActions}</span>
              </div>
              <div className="challenge-item">
                <span className="challenge-label">LOD Expressions</span>
                <span className="challenge-count">{dashboard.migrationChallenges.lodExpressions}</span>
              </div>
              <div className="challenge-item">
                <span className="challenge-label">Custom Extensions</span>
                <span className="challenge-count">{dashboard.migrationChallenges.customExtensions}</span>
              </div>
              <div className="challenge-item">
                <span className="challenge-label">Data Blending</span>
                <span className="challenge-count">{dashboard.migrationChallenges.dataBlending}</span>
              </div>
              <div className="challenge-item">
                <span className="challenge-label">Parameter Actions</span>
                <span className="challenge-count">{dashboard.migrationChallenges.parameterActions}</span>
              </div>
              <div className="challenge-item">
                <span className="challenge-label">Table Calculations</span>
                <span className="challenge-count">{dashboard.migrationChallenges.tableCalculations}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="usage-section">
          <div className="section-header">
            <h2>⓵ Usage by Time Period</h2>
            <button className="expand-btn">Click to expand ▼</button>
          </div>
          <div className="usage-controls">
            <div className="form-group-inline">
              <label>User:</label>
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
            <div className="form-group-inline">
              <label>Period:</label>
              <select value={timePeriod} onChange={(e) => setTimePeriod(e.target.value)}>
                <option value="1-week">1 Week</option>
                <option value="1-month">1 Month</option>
                <option value="3-months">3 Months</option>
                <option value="6-months">6 Months</option>
              </select>
            </div>
          </div>
        </div>

        <div className="chart-section">
          <h2>Daily Activity</h2>
          <div className="chart-container">
            <svg className="activity-chart" viewBox="0 0 800 300">
              <line x1="50" y1="250" x2="750" y2="250" stroke="#679289" strokeWidth="2" />
              <line x1="50" y1="50" x2="50" y2="250" stroke="#679289" strokeWidth="2" />
              <text x="10" y="150" fill="white" fontSize="12" transform="rotate(-90 10 150)">Unique Users</text>
              <text x="400" y="280" fill="white" fontSize="12" textAnchor="middle">Date</text>

              <polyline
                points="70,240 120,230 170,235 220,220 270,180 320,240 370,235 420,190 470,240 520,170 570,240 620,150 670,240 720,160 750,240"
                fill="none"
                stroke="#679289"
                strokeWidth="2"
              />

              <circle cx="270" cy="180" r="4" fill="#F4C095" />
              <circle cx="520" cy="170" r="4" fill="#F4C095" />
              <circle cx="620" cy="150" r="4" fill="#F4C095" />
              <circle cx="720" cy="160" r="4" fill="#F4C095" />

              {[70, 120, 170, 220, 270, 320, 370, 420, 470, 520, 570, 620, 670, 720].map((x, i) => (
                <text key={i} x={x} y="265" fill="white" fontSize="10" textAnchor="middle">
                  {`12/${i + 8}`}
                </text>
              ))}
            </svg>
          </div>
        </div>

        <div className="events-section">
          <h2>Access Events</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>DATE & TIME</th>
                  <th>USER</th>
                  <th>VIEW NAME</th>
                  <th>EVENT ID</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.accessEvents.map((event, index) => (
                  <tr key={index}>
                    <td>{event.dateTime}</td>
                    <td>{event.user}</td>
                    <td>{event.viewName}</td>
                    <td>{event.eventId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="components-section">
          <h2>Component Breakdown</h2>
          <div className="components-grid">
            <div className="component-card">
              <h4>Worksheets</h4>
              <p className="component-count">{dashboard.components.worksheets}</p>
            </div>
            <div className="component-card">
              <h4>Data Sources</h4>
              <p className="component-count">{dashboard.components.dataSources}</p>
            </div>
            <div className="component-card">
              <h4>Filters</h4>
              <p className="component-count">{dashboard.components.filters}</p>
            </div>
            <div className="component-card">
              <h4>Parameters</h4>
              <p className="component-count">{dashboard.components.parameters}</p>
            </div>
            <div className="component-card">
              <h4>Calculated Fields</h4>
              <p className="component-count">{dashboard.components.calculatedFields}</p>
            </div>
            <div className="component-card">
              <h4>Actions</h4>
              <p className="component-count">{dashboard.components.actions}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Analyzer() {
  const navigate = useNavigate();
  const [analyses] = useState([
    {
      id: 1,
      name: 'Marketing Environment',
      environment: 'Production',
      dashboards: 12,
      users: 45,
      complexity: 'Medium',
      migrationReadiness: 85,
      status: 'Completed',
      date: '2025-12-21',
      dashboardDetails: [
        {
          name: 'Campaign Performance',
          complexity: 'Low',
          usage: 'High',
          ready: true,
          project: 'Marketing Insights',
          totalViews: 156,
          uniqueUsers: 12,
          firstAccess: '2025-10-08',
          lastAccess: '2025-12-21',
          tableauComplexity: 3.2,
          migrationComplexity: 2.1,
          components: {
            worksheets: 8,
            dataSources: 3,
            filters: 6,
            parameters: 2,
            calculatedFields: 12,
            actions: 4
          },
          migrationChallenges: {
            contextFilters: 0,
            setActions: 0,
            lodExpressions: 2,
            customExtensions: 0,
            dataBlending: 1,
            parameterActions: 0,
            tableCalculations: 3
          },
          accessEvents: [
            { dateTime: '2025-12-21 14:23:45', user: 'john.smith@company.com', viewName: 'Overview', eventId: '639989105' },
            { dateTime: '2025-12-21 11:15:30', user: 'sarah.jones@company.com', viewName: 'Trends', eventId: '639987823' },
            { dateTime: '2025-12-20 16:45:12', user: 'mike.davis@company.com', viewName: 'Details', eventId: '639956432' },
            { dateTime: '2025-12-20 09:30:22', user: 'john.smith@company.com', viewName: 'Overview', eventId: '639945201' },
          ]
        },
        {
          name: 'Customer Analytics',
          complexity: 'Medium',
          usage: 'Medium',
          ready: true,
          project: 'Marketing Insights',
          totalViews: 89,
          uniqueUsers: 8,
          firstAccess: '2025-09-15',
          lastAccess: '2025-12-20',
          tableauComplexity: 5.8,
          migrationComplexity: 4.2,
          components: {
            worksheets: 12,
            dataSources: 4,
            filters: 10,
            parameters: 5,
            calculatedFields: 18,
            actions: 6
          },
          migrationChallenges: {
            contextFilters: 2,
            setActions: 1,
            lodExpressions: 5,
            customExtensions: 0,
            dataBlending: 2,
            parameterActions: 1,
            tableCalculations: 8
          },
          accessEvents: [
            { dateTime: '2025-12-20 15:30:11', user: 'emily.wilson@company.com', viewName: 'Customer Segmentation', eventId: '639967234' },
            { dateTime: '2025-12-19 13:20:45', user: 'david.lee@company.com', viewName: 'Lifetime Value', eventId: '639945678' },
          ]
        },
        {
          name: 'ROI Dashboard',
          complexity: 'High',
          usage: 'High',
          ready: false,
          project: 'Marketing Insights',
          totalViews: 203,
          uniqueUsers: 15,
          firstAccess: '2025-08-01',
          lastAccess: '2025-12-22',
          tableauComplexity: 8.5,
          migrationComplexity: 7.8,
          components: {
            worksheets: 15,
            dataSources: 6,
            filters: 14,
            parameters: 8,
            calculatedFields: 25,
            actions: 10
          },
          migrationChallenges: {
            contextFilters: 4,
            setActions: 3,
            lodExpressions: 12,
            customExtensions: 2,
            dataBlending: 4,
            parameterActions: 5,
            tableCalculations: 15
          },
          accessEvents: [
            { dateTime: '2025-12-22 10:15:33', user: 'robert.brown@company.com', viewName: 'ROI Summary', eventId: '640012345' },
            { dateTime: '2025-12-21 16:45:22', user: 'lisa.taylor@company.com', viewName: 'Cost Analysis', eventId: '639998765' },
          ]
        },
        {
          name: 'Social Media Metrics',
          complexity: 'Low',
          usage: 'Low',
          ready: true,
          project: 'Marketing Insights',
          totalViews: 34,
          uniqueUsers: 5,
          firstAccess: '2025-11-12',
          lastAccess: '2025-12-18',
          tableauComplexity: 2.1,
          migrationComplexity: 1.5,
          components: {
            worksheets: 5,
            dataSources: 2,
            filters: 4,
            parameters: 1,
            calculatedFields: 7,
            actions: 2
          },
          migrationChallenges: {
            contextFilters: 0,
            setActions: 0,
            lodExpressions: 1,
            customExtensions: 0,
            dataBlending: 0,
            parameterActions: 0,
            tableCalculations: 2
          },
          accessEvents: [
            { dateTime: '2025-12-18 14:22:11', user: 'amanda.clark@company.com', viewName: 'Social Overview', eventId: '639876543' },
          ]
        },
      ]
    },
    {
      id: 2,
      name: 'Operations Environment',
      environment: 'Production',
      dashboards: 18,
      users: 62,
      complexity: 'High',
      migrationReadiness: 72,
      status: 'Completed',
      date: '2025-12-20',
      dashboardDetails: [
        {
          name: 'Supply Chain',
          complexity: 'High',
          usage: 'High',
          ready: false,
          project: 'Operations',
          totalViews: 245,
          uniqueUsers: 18,
          firstAccess: '2025-07-15',
          lastAccess: '2025-12-22',
          tableauComplexity: 9.2,
          migrationComplexity: 8.5,
          components: { worksheets: 18, dataSources: 8, filters: 16, parameters: 10, calculatedFields: 30, actions: 12 },
          migrationChallenges: {
            contextFilters: 5,
            setActions: 4,
            lodExpressions: 15,
            customExtensions: 1,
            dataBlending: 6,
            parameterActions: 3,
            tableCalculations: 18
          },
          accessEvents: [
            { dateTime: '2025-12-22 09:15:22', user: 'ops.manager@company.com', viewName: 'Supply Overview', eventId: '640011223' },
          ]
        },
        {
          name: 'Inventory Tracking',
          complexity: 'Medium',
          usage: 'High',
          ready: true,
          project: 'Operations',
          totalViews: 189,
          uniqueUsers: 14,
          firstAccess: '2025-08-20',
          lastAccess: '2025-12-21',
          tableauComplexity: 5.5,
          migrationComplexity: 3.8,
          components: { worksheets: 10, dataSources: 4, filters: 8, parameters: 4, calculatedFields: 15, actions: 5 },
          migrationChallenges: {
            contextFilters: 1,
            setActions: 1,
            lodExpressions: 4,
            customExtensions: 0,
            dataBlending: 2,
            parameterActions: 1,
            tableCalculations: 6
          },
          accessEvents: [
            { dateTime: '2025-12-21 13:45:11', user: 'inventory.lead@company.com', viewName: 'Stock Levels', eventId: '639998877' },
          ]
        },
        {
          name: 'Order Processing',
          complexity: 'Medium',
          usage: 'Medium',
          ready: true,
          project: 'Operations',
          totalViews: 112,
          uniqueUsers: 10,
          firstAccess: '2025-09-10',
          lastAccess: '2025-12-20',
          tableauComplexity: 4.7,
          migrationComplexity: 3.2,
          components: { worksheets: 9, dataSources: 3, filters: 7, parameters: 3, calculatedFields: 11, actions: 4 },
          migrationChallenges: {
            contextFilters: 1,
            setActions: 0,
            lodExpressions: 3,
            customExtensions: 0,
            dataBlending: 1,
            parameterActions: 1,
            tableCalculations: 5
          },
          accessEvents: [
            { dateTime: '2025-12-20 11:30:45', user: 'order.processor@company.com', viewName: 'Order Status', eventId: '639967788' },
          ]
        },
      ]
    },
    {
      id: 3,
      name: 'Finance Environment',
      environment: 'Production',
      dashboards: 8,
      users: 28,
      complexity: 'Low',
      migrationReadiness: 95,
      status: 'In Progress',
      date: '2025-12-22',
      dashboardDetails: [
        {
          name: 'Budget Overview',
          complexity: 'Low',
          usage: 'High',
          ready: true,
          project: 'Finance',
          totalViews: 178,
          uniqueUsers: 16,
          firstAccess: '2025-09-01',
          lastAccess: '2025-12-22',
          tableauComplexity: 3.5,
          migrationComplexity: 2.3,
          components: { worksheets: 6, dataSources: 2, filters: 5, parameters: 2, calculatedFields: 8, actions: 3 },
          migrationChallenges: {
            contextFilters: 0,
            setActions: 0,
            lodExpressions: 2,
            customExtensions: 0,
            dataBlending: 0,
            parameterActions: 1,
            tableCalculations: 3
          },
          accessEvents: [
            { dateTime: '2025-12-22 08:30:15', user: 'finance.director@company.com', viewName: 'Budget Summary', eventId: '640015678' },
          ]
        },
        {
          name: 'Expense Tracking',
          complexity: 'Low',
          usage: 'Medium',
          ready: true,
          project: 'Finance',
          totalViews: 95,
          uniqueUsers: 11,
          firstAccess: '2025-10-05',
          lastAccess: '2025-12-21',
          tableauComplexity: 2.8,
          migrationComplexity: 1.9,
          components: { worksheets: 5, dataSources: 2, filters: 4, parameters: 1, calculatedFields: 6, actions: 2 },
          migrationChallenges: {
            contextFilters: 0,
            setActions: 0,
            lodExpressions: 1,
            customExtensions: 0,
            dataBlending: 0,
            parameterActions: 0,
            tableCalculations: 2
          },
          accessEvents: [
            { dateTime: '2025-12-21 10:15:33', user: 'expense.analyst@company.com', viewName: 'Expense Details', eventId: '639995544' },
          ]
        },
      ]
    }
  ]);

  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState(null);

  const handleViewAnalysis = (id) => {
    const analysis = analyses.find(a => a.id === id);
    setSelectedAnalysis(analysis);
    navigate('/analyzer/detail');
  };

  const handleViewDashboard = (dashboard) => {
    setSelectedDashboard(dashboard);
    navigate('/analyzer/dashboard');
  };

  const handleNewAnalysis = () => {
    navigate('/analyzer/new');
  };

  const handleBack = () => {
    setSelectedAnalysis(null);
    navigate('/analyzer');
  };

  const handleBackToDashboardList = () => {
    setSelectedDashboard(null);
    navigate('/analyzer/detail');
  };

  const handleSubmitAnalysis = (formData) => {
    alert('Analysis started! In a real app, this would connect to Tableau and run the analysis.');
    navigate('/analyzer');
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <AnalyzerList
            analyses={analyses}
            onViewAnalysis={handleViewAnalysis}
            onNewAnalysis={handleNewAnalysis}
          />
        }
      />
      <Route
        path="/detail"
        element={
          selectedAnalysis ? (
            <AnalysisDetail
              analysis={selectedAnalysis}
              onBack={handleBack}
              onViewDashboard={handleViewDashboard}
            />
          ) : (
            <AnalyzerList
              analyses={analyses}
              onViewAnalysis={handleViewAnalysis}
              onNewAnalysis={handleNewAnalysis}
            />
          )
        }
      />
      <Route
        path="/dashboard"
        element={
          selectedDashboard ? (
            <DashboardDetail
              dashboard={selectedDashboard}
              onBack={handleBackToDashboardList}
            />
          ) : (
            <AnalyzerList
              analyses={analyses}
              onViewAnalysis={handleViewAnalysis}
              onNewAnalysis={handleNewAnalysis}
            />
          )
        }
      />
      <Route
        path="/new"
        element={
          <NewAnalysis onCancel={handleBack} onSubmit={handleSubmitAnalysis} />
        }
      />
    </Routes>
  );
}

export default Analyzer;
